import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Sparkles } from "lucide-react";
import { askData, askHistory } from "@/lib/api";
import SimpleMarkdown from "@/components/SimpleMarkdown";

// Ask your data (backend routes/ask_data.py): type a question, Claude answers from FPA's numbers for the
// gyms you manage, with the figures behind it and a next step. Read-only: it can't change anything.

const EXAMPLES = [
  "Which plan has the most people not renewing after it ends?",
  "Why did evening attendance drop in September?",
  "Which gym converts consults best, and what objections come up most?",
  "Which members are most likely to leave this month, and what are they worth?",
  "How did revenue and ad spend compare month by month this quarter?",
];
const LOOKUP = { list_gyms: "gyms", gym_numbers: "gym numbers", monthly_kpis: "monthly report", sales: "sales", consults: "consults",
  leads: "leads", attendance: "check-ins", members: "members", at_risk: "at-risk list", upsell: "upsell list" };

export default function AskData() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState(null);
  useEffect(() => { askHistory().then((d) => { setItems(d.history || []); setLimit(d.daily_limit); }).catch(() => {}); }, []);

  const ask = async (question, followUp = false) => {
    const text = (question || "").trim();
    if (text.length < 3 || busy) return;
    setBusy(true);
    try {
      const r = await askData({ question: text, follow_up: followUp });
      setItems((it) => [r, ...it]);
      setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't get an answer");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="ask-page">
      <div>
        <div className="eyebrow inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-violet-300" />Ask your data</div>
        <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Ask a question</h2>
        <p className="text-sm text-zinc-400 mt-1">
          Ask anything about your gyms in plain words. The answer comes from FPA&apos;s numbers, with the figures behind it.
          It can only read, never change anything.{limit ? ` Up to ${limit} questions a day.` : ""}
        </p>
      </div>

      <div className="card-surface p-4 space-y-3">
        <textarea value={q} onChange={(e) => setQ(e.target.value)} rows={3} maxLength={1000} disabled={busy}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) ask(q, false); }}
          placeholder="e.g. Which gym had the most members not renewing last month, and why?"
          className="w-full bg-[#0a0a0a] border border-[#262626] rounded-lg px-3 py-2 text-sm text-zinc-100" data-testid="ask-input" />
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => ask(q, false)} disabled={busy || q.trim().length < 3} data-testid="ask-submit"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{busy ? "Working it out" : "Ask"}
          </button>
          {items.length > 0 && (
            <button type="button" onClick={() => ask(q, true)} disabled={busy || q.trim().length < 3}
              className="h-10 px-4 rounded-full border border-[#2a2a2a] text-sm text-zinc-300 disabled:opacity-50">Ask as a follow-up</button>
          )}
          {busy && <span className="text-xs text-zinc-500">Looking through the numbers. This can take up to a minute.</span>}
        </div>
        {!items.length && (
          <div className="flex flex-wrap gap-2 pt-1">
            {EXAMPLES.map((ex) => (
              <button key={ex} type="button" onClick={() => setQ(ex)} className="text-xs px-3 py-1.5 rounded-full border border-[#262626] text-zinc-400 hover:text-zinc-100">{ex}</button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {items.map((it) => (
          <article key={it.id} className="card-surface p-5" data-testid="ask-answer">
            <div className="text-sm text-violet-200 font-medium">{it.question}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {new Date(it.at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
              {it.calls?.length ? ` · looked at ${[...new Set(it.calls.map((c) => LOOKUP[c.tool] || c.tool))].join(", ")}` : ""}
            </div>
            <div className="mt-3 text-sm"><SimpleMarkdown text={it.answer} /></div>
          </article>
        ))}
      </div>
    </div>
  );
}
